import React, { ReactElement } from 'react';
import {
  IntervalType,
  SubscriptionPlanId,
  TeamPermission,
} from 'nekst-api';
import { useAuthorizationChecker } from 'authorization-scope';

import BorderedBox from '../../shared/web/layout/BorderedBox';
import styles from './BillingSettings.module.scss';
import Separator from '../../shared/web/uitheme/form/Separator';
import { useBillingContext } from './BillingPageContext';
import useFeatureToggleService from 'feature-toggle';

function AbstractPlanWidget(
  props: {
    subscriptionPlanId: SubscriptionPlanId
    features: (string | ReactElement)[],
    featuresBlock?: ReactElement,
    showIsCurrent: boolean,
    isLegacy?: boolean,
  },
) {
  const billingData = useBillingContext()
    .getData();

  const subscriptionPlan = billingData.availablePlans
    .find((item) => item.id === props.subscriptionPlanId)!;

  const monthPrice = subscriptionPlan.prices.find(
    (item) => item.recurringInterval === IntervalType.MONTH,
  )!;

  const yearPrice = subscriptionPlan.prices.find(
    (item) => item.recurringInterval === IntervalType.YEAR,
  );

  const isCurrent = billingData.teamBilling.planId === props.subscriptionPlanId && props.showIsCurrent;

  let priceElement;
  if (monthPrice.amount === 0) {
    priceElement = <span>Free</span>;
  } else {
    const currentPrice = isCurrent ? billingData.teamBilling.price?.value : monthPrice.amount;

    if (isCurrent) {
      priceElement = (
        <div>${currentPrice}/{billingData.teamBilling.billingInterval === IntervalType.MONTH ? 'mo' : 'year'}</div>
      );
    } else {
      priceElement = (<>
        <div>${monthPrice.amount}/mo</div>
        <div className={styles.priceLine2}>(or ${Math.round(yearPrice!.amount / 12 * 100) / 100}/mo paid annually)</div>
      </>);
    }

  }

  let title = subscriptionPlan.name;

  if (props.isLegacy) {
    title += ' Legacy';
  }

  if (isCurrent) {
    title += ' (Current Plan)';
  }

  return (
    <BorderedBox title={title} className={styles.item}>
      <div className={styles.price}>
        {priceElement}
      </div>
      <Separator />
      <ul className={styles.features}>
        {props.features.map((feature) => {
          if (typeof feature === 'string') {
            return (
              <li key={feature}>
                {feature}
              </li>
            );
          } else {
            return feature;
          }
        })}
      </ul>
    </BorderedBox>
  );
}

function AgentLitePlanWidgetNew(props: {
  showIsCurrent: boolean,
}) {
  return (
    <AbstractPlanWidget
      subscriptionPlanId={SubscriptionPlanId.AGENT_LITE}
      showIsCurrent={props.showIsCurrent}
      features={[
        'Single User Account',
        'Manage up to 5 Total Transactions',
        '4 Pre-Loaded, Customizable Workflows',
        'Unlimited Workflows',
        'Client Portals',
        'Assign Tasks only to Yourself',
        <b className={styles.sectionTitle}>Does Not Include</b>,
        'SMS Tasks',
        'Create Transactions by Uploading a Signed Contract',
      ]}
    />
  );
}

function AgentProWidgetNew(props: {
  showIsCurrent: boolean
}) {
  return (
    <AbstractPlanWidget
      subscriptionPlanId={SubscriptionPlanId.AGENT_PRO}
      showIsCurrent={props.showIsCurrent}
      features={[
        <b className={styles.sectionTitle}>Everything in Solo Lite Plus</b>,
        '$299 One-Time Onboarding Fee (waived with an Annual Plan)',
        'Create Transactions by Uploading a Signed Contract',
        'Sync Contract Dates to Workflow Tasks',
        'Supports Contracts from up to 3 Markets of Operation',
        'Unlimited Transactions',
        'SMS Messaging',
      ]}
    />
  );
}

function TeamProWidgetNew(props: {
  showIsCurrent: boolean
}) {
  return (
    <AbstractPlanWidget
      subscriptionPlanId={SubscriptionPlanId.TEAM_PRO}
      showIsCurrent={props.showIsCurrent}
      features={[
        <b className={styles.sectionTitle}>Everything in Solo Pro Plus</b>,
        '$399 One-Time Onboarding Fee (waived with an Annual Plan)',
        'Collaborate with Multiple Team Members',
        'Assign Tasks to any Team Member',
        'Tag Team Members in Comments & Notes',
        'Customize Notifiacations',
        'Support Contracts from up to 6 Markets of Operation',
        <li className={styles.feature}>
          Includes 4 Seats:
          <ul>
            <li>1 x Account Owner</li>
            <li>1 x Administrator</li>
            <li>2 x Team Members</li>
          </ul>
        </li>,
        <li className={styles.feature}>
          Additional Seats can be purchased:
          <ul>
            <li>
              $49/mo per Administrator
            </li>
            <li>
              $19/mo per Team Member
            </li>
          </ul>
        </li>,
        <li className={styles.feature}>
          <a href="https://nekst.com/pricing" target="_blank" rel="noreferrer">
            Compare types of seats
          </a>
        </li>
      ]}
    />
  );
}

function AgentLitePlanWidget() {
  return (
    <AbstractPlanWidget
      showIsCurrent
      subscriptionPlanId={SubscriptionPlanId.AGENT_LITE}
      features={[
        'Up to 5 Total Transactions',
        'Intuitive Task Management System',
        'Calendar Integration',
        'Unlimited Contacts',
        '4 Pre-loaded Workflows',
        'Unlimited Custom Workflows',
        'Email Notifications',
        'Support: Chat',
      ]}
    />
  );
}

function AgentProWidget(props: {
  isLegacy?: boolean,
}) {
  return (
    <AbstractPlanWidget
      showIsCurrent
      subscriptionPlanId={SubscriptionPlanId.AGENT_PRO}
      isLegacy={props.isLegacy}
      features={[
        <>
          <b>Unlimited</b>
          {' '}
          Transactions
        </>,
        'Intuitive Task Management System',
        'Calendar Integration',
        'Unlimited Contacts',
        '4 Pre-loaded Workflows',
        'Unlimited Custom Workflows',
        'Email Notifications',
        'Support: Chat',
      ]}
    />
  );
}

function TeamProWidget(props: {
  isLegacy?: boolean
}) {
  return (
    <AbstractPlanWidget
      showIsCurrent
      subscriptionPlanId={SubscriptionPlanId.TEAM_PRO}
      isLegacy={props.isLegacy}
      features={[
        <>
          <b>Unlimited</b>
          {' '}
          Transactions
        </>,
        'Intuitive Task Management System',
        <>
          Assign Tasks to
          {' '}
          <b>Any Party</b>
        </>,
        'Calendar Integration',
        'Unlimited Contacts',
        '4 Pre-loaded Workflows',
        'Unlimited Custom Workflows',
        'Email Notifications',
        'Support: Chat & Phone',
      ]}
    />
  );
}

function LegacyWidget() {
  const billingData = useBillingContext()
    .getData();

  const planId = billingData.teamBilling.planId;

  if (planId === SubscriptionPlanId.TEAM_PRO) {
    return (<TeamProWidget isLegacy />);
  } else if (planId === SubscriptionPlanId.AGENT_PRO) {
    return (<AgentProWidget isLegacy />);
  } else {
    return null;
  }
}

export default function SubscriptionWorkflowsWidget() {
  const { isGranted } = useAuthorizationChecker();

  const featureToggleService = useFeatureToggleService();

  if (isGranted(TeamPermission.MANAGE_BILLING)) {
    const isActualSubscription = isGranted(TeamPermission.HAS_ACTUAL_SUBSCRIPTION);

    return (
      <div className={styles.subscriptions}>
        {featureToggleService.isNewUiEnabled() ? (
          <>
            <AgentLitePlanWidgetNew showIsCurrent={isActualSubscription} />
            <AgentProWidgetNew showIsCurrent={isActualSubscription} />
            <TeamProWidgetNew showIsCurrent={isActualSubscription} />
            {!isActualSubscription && <LegacyWidget />}
          </>
        ) : (
          <>
            <AgentLitePlanWidget />
            <AgentProWidget />
            <TeamProWidget />
          </>
        )}
      </div>
    );
  } else {
    return null;
  }
}

